import { useAdminApi } from '@hooks/useAdminApi';
import { Order } from '@protos/v2/order';
import { useEffect, useState } from 'react';

type SymbolInfo = {
  symbol: string;
  expiry: string;
};

export const useOnyxBookData = () => {
  const { apiClient } = useAdminApi();

  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [symbolsPerProduct, setSymbolsPerProduct] = useState<Map<string, SymbolInfo[]>>(new Map());
  const [ordersPerSymbol, setOrdersPerSymbol] = useState<Map<string, Order[]>>(new Map());

  useEffect(() => {
    const fetchOrders = async () => {
      const orders = await apiClient?.ladderLoader({ is_live: true }).loadAllData();
      if (!orders) return;

      setOrders(orders);
    };

    fetchOrders();

    // Refetch data every 5s
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [apiClient]);

  useEffect(() => {
    const newProducts: string[] = [];
    const productMap = new Map<string, SymbolInfo[]>();
    const ordersMap = new Map<string, Order[]>();

    orders.forEach(order => {
      const stringifiedSymbol = typeof order.symbol === 'string' ? order.symbol : `${order.symbol.front}-${order.symbol.back}`;
      const expiry = order.expiry_timestamp || '';

      if (!productMap.has(order.product_symbol)) {
        newProducts.push(order.product_symbol);
        productMap.set(order.product_symbol, []);
      }

      const productSymbols = productMap.get(order.product_symbol)!;
      const existingSymbolIndex = productSymbols.findIndex(s => s.symbol === stringifiedSymbol);

      if (existingSymbolIndex === -1) {
        productSymbols.push({ symbol: stringifiedSymbol, expiry });
      } else if (expiry < productSymbols[existingSymbolIndex].expiry) {
        productSymbols[existingSymbolIndex] = { symbol: stringifiedSymbol, expiry };
      }

      if (!ordersMap.has(stringifiedSymbol)) {
        ordersMap.set(stringifiedSymbol, []);
      }
      ordersMap.get(stringifiedSymbol)!.push(order);
    });

    setProducts(newProducts);
    setSymbolsPerProduct(productMap);
    setOrdersPerSymbol(ordersMap);
  }, [orders]);

  return { orders, products, symbolsPerProduct, ordersPerSymbol };
};
